import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, FileArchive, Trash2, ChevronRight, CheckCircle2, AlertTriangle, Loader2, PackageOpen, Check, ListChecks, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { apiGet, apiDelete, apiUpload, apiPut, apiPost } from "@/lib/api";

function ComErpField({ comanda, onSaved }) {
  const [val, setVal] = useState(comanda.com_erp || "");
  const [saving, setSaving] = useState(false);
  const dirty = (val || "").trim() !== (comanda.com_erp || "").trim();

  const save = async (e) => {
    e.stopPropagation();
    if (!dirty || saving) return;
    setSaving(true);
    try {
      const clean = (val || "").trim();
      await apiPut(`/comenzi/${comanda.id}/com-erp`, { com_erp: clean });
      toast.success(clean ? `COM-ERP salvat: ${clean}` : "COM-ERP sters");
      onSaved(comanda.id, clean);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="mt-3 flex items-center gap-2"
      onClick={(e) => e.stopPropagation()}
      data-testid={`com-erp-row-${comanda.nr_comanda}`}
    >
      <span className="text-[11px] font-semibold text-slate-500 shrink-0">COM-ERP</span>
      <Input
        value={val}
        onChange={(e) => setVal(e.target.value.replace(/[^0-9]/g, "").slice(0, 12))}
        onKeyDown={(e) => { if (e.key === "Enter") save(e); }}
        placeholder="ex: 303485"
        inputMode="numeric"
        data-testid={`com-erp-input-${comanda.nr_comanda}`}
        className="h-9 text-base font-semibold tracking-wide tabular-nums w-full max-w-[200px]"
      />
      <Button
        size="icon"
        variant={dirty ? "default" : "ghost"}
        disabled={!dirty || saving}
        onClick={save}
        data-testid={`com-erp-save-${comanda.nr_comanda}`}
        className={`h-8 w-8 shrink-0 ${dirty ? "bg-amber-500 hover:bg-amber-600 text-white" : "text-slate-300"}`}
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
      </Button>
    </div>
  );
}

export default function ComenziPage() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [comenzi, setComenzi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGet("/comenzi");
      setComenzi((d.items || []).filter((c) => c.sursa !== "obloane"));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allSelected = comenzi.length > 0 && selected.size === comenzi.length;

  const toggleSelectAll = () => {
    setSelected((prev) => (prev.size === comenzi.length ? new Set() : new Set(comenzi.map((c) => c.id))));
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected(new Set());
  };

  const doBulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Stergi ${ids.length} comenzi selectate? Vor disparea si din pagina Documente.`)) return;
    setBulkDeleting(true);
    try {
      const r = await apiPost("/comenzi/bulk-delete", { ids });
      toast.success(`${r.deleted} comenzi sterse`);
      exitSelectMode();
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBulkDeleting(false);
    }
  };

  const doUpload = async (file) => {
    if (!file) return;
    if (!/\.amelprod(\.zip)?$/i.test(file.name)) {
      toast.error("Alege un fisier .amelprod");
      return;
    }
    setUploading(true);
    try {
      const r = await apiUpload("/comenzi/import", file);
      if (!r.checksum_ok) {
        toast.warning(`Comanda ${r.nr_comanda} importata, dar checksum-ul NU se potriveste.`);
      } else {
        toast.success(`${r.inlocuit ? "Actualizata" : "Importata"}: ${r.nr_comanda} (${r.nr_pozitii} pozitii)`);
      }
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const doDelete = async (e, id, nr) => {
    e.stopPropagation();
    if (!window.confirm(`Stergi comanda ${nr}?`)) return;
    try {
      await apiDelete(`/comenzi/${id}`);
      toast.success(`Comanda ${nr} stearsa`);
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const onComErpSaved = useCallback((id, com_erp) => {
    setComenzi((prev) => prev.map((c) => (c.id === id ? { ...c, com_erp } : c)));
  }, []);

  return (
    <div data-testid="comenzi-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Comenzi productie</h1>
        <p className="text-sm text-slate-500 mt-1">
          Ataseaza un fisier .amelprod. Numarul comenzii preia exact numele fisierului.
        </p>
      </div>

      <Card
        data-testid="dropzone"
        onClick={() => !uploading && fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); doUpload(e.dataTransfer.files?.[0]); }}
        className={`mb-8 cursor-pointer border-2 border-dashed transition-colors ${
          dragOver ? "border-amber-500 bg-amber-50" : "border-slate-300 bg-white hover:border-amber-400"
        }`}
      >
        <div className="py-10 flex flex-col items-center justify-center text-center px-4">
          <div className="h-14 w-14 rounded-full bg-amber-100 flex items-center justify-center mb-4">
            {uploading ? <Loader2 className="h-7 w-7 text-amber-600 animate-spin" /> : <Upload className="h-7 w-7 text-amber-600" />}
          </div>
          <p className="font-semibold text-slate-800">
            {uploading ? "Se importa fisierul..." : "Trage fisierul .amelprod aici sau apasa pentru a alege"}
          </p>
          <p className="text-xs text-slate-400 mt-1">Format acceptat: .amelprod</p>
          <input
            ref={fileRef}
            type="file"
            accept=".amelprod,.zip"
            className="hidden"
            data-testid="file-input"
            onChange={(e) => doUpload(e.target.files?.[0])}
          />
        </div>
      </Card>

      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <h2 className="text-base font-semibold text-slate-700">
          Comenzi importate {comenzi.length > 0 && <span className="text-slate-400">({comenzi.length})</span>}
        </h2>
        {comenzi.length > 0 && (
          <div className="flex items-center gap-2">
            {!selectMode ? (
              <Button
                variant="outline" size="sm"
                data-testid="select-mode-toggle"
                onClick={() => setSelectMode(true)}
                className="h-8 gap-1.5"
              >
                <ListChecks className="h-4 w-4" /> Selecteaza
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost" size="sm"
                  data-testid="select-all-btn"
                  onClick={toggleSelectAll}
                  className="h-8"
                >
                  {allSelected ? "Deselecteaza tot" : "Selecteaza tot"}
                </Button>
                <Button
                  variant="destructive" size="sm"
                  data-testid="bulk-delete-btn"
                  disabled={selected.size === 0 || bulkDeleting}
                  onClick={doBulkDelete}
                  className="h-8 gap-1.5"
                >
                  {bulkDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  Sterge ({selected.size})
                </Button>
                <Button
                  variant="ghost" size="icon"
                  data-testid="select-cancel-btn"
                  onClick={exitSelectMode}
                  className="h-8 w-8 text-slate-400"
                >
                  <X className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-16 flex justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : comenzi.length === 0 ? (
        <Card className="py-16 flex flex-col items-center text-slate-400" data-testid="empty-state">
          <PackageOpen className="h-10 w-10 mb-3" />
          <p className="text-sm">Nicio comanda importata inca.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="comenzi-list">
          {comenzi.map((c) => {
            const isSel = selected.has(c.id);
            return (
            <Card
              key={c.id}
              data-testid={`comanda-card-${c.nr_comanda}`}
              onClick={() => (selectMode ? toggleSelect(c.id) : navigate(`/comanda/${c.id}`))}
              className={`p-4 cursor-pointer hover:shadow-md transition-all group ${
                selectMode && isSel
                  ? "border-amber-400 bg-amber-50 ring-1 ring-amber-300"
                  : c.com_erp
                  ? "border-emerald-300 bg-emerald-50/40 hover:border-emerald-400"
                  : "hover:border-amber-300"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  {selectMode && (
                    <Checkbox
                      checked={isSel}
                      onClick={(e) => e.stopPropagation()}
                      onCheckedChange={() => toggleSelect(c.id)}
                      data-testid={`comanda-check-${c.nr_comanda}`}
                      className="mt-2 shrink-0"
                    />
                  )}
                  <div className="h-10 w-10 rounded-lg bg-slate-900 flex items-center justify-center shrink-0">
                    <FileArchive className="h-5 w-5 text-amber-400" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-bold text-slate-900 truncate">{c.nr_comanda}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {c.client_nume || "(fara client)"}{c.client_localitate ? ` · ${c.client_localitate}` : ""}
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Badge variant="secondary" className="text-[11px]">{c.nr_pozitii} pozitii</Badge>
                      {c.serie && <Badge variant="outline" className="text-[11px]">{c.serie}</Badge>}
                      {c.esenta && <Badge variant="outline" className="text-[11px]">{c.esenta}</Badge>}
                      {c.checksum_ok ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> verificat</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] text-amber-600"><AlertTriangle className="h-3.5 w-3.5" /> checksum</span>
                      )}
                      {c.com_erp && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[11px] font-semibold tabular-nums"
                          data-testid={`com-erp-badge-${c.nr_comanda}`}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" /> COM-ERP {c.com_erp}
                        </span>
                      )}
                    </div>
                    <ComErpField comanda={c} onSaved={onComErpSaved} />
                  </div>
                </div>
                {!selectMode && (
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost" size="icon"
                      className="h-8 w-8 text-slate-400 hover:text-red-600"
                      data-testid={`delete-comanda-${c.nr_comanda}`}
                      onClick={(e) => doDelete(e, c.id, c.nr_comanda)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-amber-500 transition-colors" />
                  </div>
                )}
              </div>
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
